import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { VIEW } from "@/config/designer";

interface WorkbenchProps {
  width: number;
  depth: number;
}

/**
 * A frosted glass bench for the blank to sit on.
 *
 * It exists to make the mounting side obvious: the hold rests on it, the
 * measurement grid sits inside it, and you can just see the rear face through
 * the glass. It stays deliberately quiet — nothing here should compete with
 * the workpiece.
 */
export function Workbench({ width, depth }: WorkbenchProps): React.ReactElement {
  const benchWidth = width + VIEW.benchMargin * 2;
  const benchDepth = depth + VIEW.benchMargin * 2;

  const gridTexture = useMemo(
    () => createGridTexture(width, depth, VIEW.benchMargin, VIEW.gridPitch),
    [width, depth],
  );

  useEffect(() => () => gridTexture.dispose(), [gridTexture]);

  return (
    <group>
      {/* Measurement grid, printed on the underside of the glass. */}
      <mesh
        position={[width / 2, -VIEW.benchThickness + 0.6, depth / 2]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow={false}
      >
        <planeGeometry args={[benchWidth, benchDepth]} />
        <meshBasicMaterial map={gridTexture} transparent opacity={0.5} depthWrite={false} />
      </mesh>

      {/* The slab. Its top sits a fraction below y = 0 so it never z-fights
          with the hold's rear face, which is exactly on the plane. */}
      <mesh
        position={[width / 2, -VIEW.benchThickness / 2 - 0.2, depth / 2]}
        receiveShadow
        renderOrder={2}
      >
        <boxGeometry args={[benchWidth, VIEW.benchThickness, benchDepth]} />
        <meshPhysicalMaterial
          color="#9fa9a3"
          transparent
          opacity={0.26}
          roughness={0.38}
          metalness={0}
          clearcoat={0.7}
          clearcoatRoughness={0.42}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>

      {/* A hairline along the top edge reads as the polished lip of the glass. */}
      <lineSegments position={[width / 2, -0.2, depth / 2]} renderOrder={3}>
        <edgesGeometry
          args={[new THREE.BoxGeometry(benchWidth, VIEW.benchThickness, benchDepth)]}
        />
        <lineBasicMaterial color="#c8d7bc" transparent opacity={0.22} depthWrite={false} />
      </lineSegments>
    </group>
  );
}

/** Fine ruled measurement grid, generated on a canvas so nothing is fetched. */
function createGridTexture(
  holdWidth: number,
  holdDepth: number,
  margin: number,
  pitch: number,
): THREE.CanvasTexture {
  const pixelsPerMm = 2;
  const width = holdWidth + margin * 2;
  const depth = holdDepth + margin * 2;
  const minX = -margin;
  const maxX = holdWidth + margin;
  const minZ = -margin;
  const maxZ = holdDepth + margin;
  const w = Math.max(2, Math.round(width * pixelsPerMm));
  const h = Math.max(2, Math.round(depth * pixelsPerMm));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.clearRect(0, 0, w, h);
    const toX = (x: number): number => (x - minX) * pixelsPerMm;
    const toY = (z: number): number => (z - minZ) * pixelsPerMm;
    const firstMultiple = (min: number, step: number): number => Math.ceil(min / step) * step;
    const drawGrid = (step: number, colour: string, lineWidth: number): void => {
      ctx.strokeStyle = colour;
      ctx.lineWidth = lineWidth;
      for (let x = firstMultiple(minX, step); x <= maxX; x += step) {
        const px = toX(x) + 0.5;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, h);
        ctx.stroke();
      }
      for (let z = firstMultiple(minZ, step); z <= maxZ; z += step) {
        const py = toY(z) + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.lineTo(w, py);
        ctx.stroke();
      }
    };

    // 10 mm subdivisions stay quiet; 50 mm and 250 mm carry the hierarchy.
    drawGrid(10, "rgba(200, 215, 188, 0.075)", 1);
    drawGrid(pitch, "rgba(200, 215, 188, 0.34)", 1);
    drawGrid(pitch * 5, "rgba(200, 215, 188, 0.58)", 1.6);

    // Fine ruler text is measured from the hold's rear-left corner, so 0 is a
    // useful modelling datum rather than an arbitrary edge of the glass.
    const fontSize = Math.max(11, Math.round(pitch * pixelsPerMm * 0.13));
    ctx.font = `500 ${fontSize}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = "rgba(225, 232, 218, 0.72)";
    ctx.strokeStyle = "rgba(225, 232, 218, 0.55)";
    ctx.lineWidth = 1;
    ctx.textBaseline = "middle";

    const rulerOffset = 20;
    const tick = 5 * pixelsPerMm;
    const xRulerY = toY(minZ + rulerOffset);
    ctx.textAlign = "center";
    for (let x = firstMultiple(minX, pitch); x <= maxX; x += pitch) {
      const px = toX(x);
      ctx.beginPath();
      ctx.moveTo(px, xRulerY - tick);
      ctx.lineTo(px, xRulerY + tick);
      ctx.stroke();
      ctx.fillText(String(x), px, xRulerY + tick + fontSize * 0.75);
    }

    const zRulerX = toX(minX + rulerOffset);
    ctx.textAlign = "right";
    for (let z = firstMultiple(minZ, pitch); z <= maxZ; z += pitch) {
      const py = toY(z);
      ctx.beginPath();
      ctx.moveTo(zRulerX - tick, py);
      ctx.lineTo(zRulerX + tick, py);
      ctx.stroke();
      ctx.fillText(String(z), zRulerX - tick - 3, py);
    }

    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(225, 232, 218, 0.5)";
    ctx.fillText("X · mm", toX(maxX - 42), xRulerY + tick + fontSize * 0.75);
    ctx.fillText("Z · mm", zRulerX + tick + 4, toY(maxZ - 14));
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}
